/**
 * CUAConfirmationDialog — Modal asking user confirmation for CUA write actions
 * Reference: TASK-UI-CU-002-1, SOL-UI-CU-002
 */

import React from 'react';
import { useCUAConfirmation } from '../hooks/useCUAConfirmation';

export function CUAConfirmationDialog() {
  const { pendingRequest, approve, reject, timeoutMs } = useCUAConfirmation();

  if (!pendingRequest) return null;

  const totalMs = (pendingRequest.timeout_sec || 10) * 1000;
  const progress = totalMs > 0 ? Math.min(100, Math.max(0, (timeoutMs / totalMs) * 100)) : 0;
  const secondsLeft = Math.max(0, Math.ceil(timeoutMs / 1000));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cua-confirm-title"
      data-testid="cua-confirmation-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4"
    >
      <div className="bg-surface-elevated bg-background rounded-xl shadow-2xl max-w-sm w-full p-5 space-y-4 border border-border">
        <div className="flex items-start gap-3">
          <span className="text-warning text-xl shrink-0" aria-hidden="true">
            ⚠️
          </span>
          <div className="flex-1 min-w-0">
            <h2 id="cua-confirm-title" className="font-semibold text-sm leading-snug">
              Agent yêu cầu thao tác màn hình
            </h2>
            <p className="text-muted text-text-03 text-xs mt-1">
              {pendingRequest.description || 'Thao tác cần xác nhận bảo mật.'}
            </p>
          </div>
        </div>

        <div className="bg-surface-neutral bg-background-neutral-01 rounded p-2 text-xs font-mono space-y-1 border border-border">
          <div className="flex justify-between items-center">
            <span className="text-muted text-text-03">Action:</span>
            <span
              data-testid="cua-confirm-action"
              className="text-warning font-semibold"
            >
              {pendingRequest.action}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-muted text-text-03">Target:</span>
            <span
              data-testid="cua-confirm-target"
              className="truncate max-w-[180px]"
              title={pendingRequest.target}
            >
              {pendingRequest.target}
            </span>
          </div>
        </div>

        {/* Countdown progress bar */}
        <div className="space-y-1">
          <div className="w-full bg-border rounded-full h-1.5 overflow-hidden">
            <div
              data-testid="cua-confirm-progress"
              className="bg-warning h-full rounded-full transition-all duration-100 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p
            data-testid="cua-confirm-countdown"
            className="text-xs text-muted text-text-03 text-center"
          >
            Tự động từ chối sau {secondsLeft}s
          </p>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            id="cua-confirm-reject-btn"
            data-testid="cua-confirm-reject-btn"
            type="button"
            onClick={reject}
            className="flex-1 px-3 py-2 text-sm font-medium border border-border rounded-lg hover:bg-background-neutral-01 transition-colors cursor-pointer"
          >
            Từ chối
          </button>
          <button
            id="cua-confirm-approve-btn"
            data-testid="cua-confirm-approve-btn"
            type="button"
            onClick={approve}
            className="flex-1 px-3 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors cursor-pointer"
          >
            Cho phép
          </button>
        </div>
      </div>
    </div>
  );
}
